/**
 * Pantalla de marcación de Staff (004 · US1 · T029/T030; US2 · T036; US6 · T067/T068;
 * US7 · T071/T072). Es la que compone todo:
 *
 * - **apertura directa de la cámara**, sin hoja previa: el toque en MARCAR es el gesto del usuario y
 *   el permiso ya se explicó al terminar el registro (`prepare-permissions.ts`);
 * - **cámara a pantalla completa** con marco, barra de progreso y textos (`camera-stage.tsx`);
 * - **éxito** (`success-screen.tsx`);
 * - **explicación de ubicación** (US6/T067): una hoja **sobre** la cámara, la primera vez que se
 *   pide en la sesión; no bloquea nada, la ubicación ya se pidió en paralelo (R-24);
 * - **discrepancia de acción** (`ACTION_CHANGED`, US2/T036): una hoja **sobre** la cámara (nunca
 *   la desmonta) con la acción correcta y un botón de confirmación explícita; hasta que el
 *   trabajador confirma no se vuelve a buscar el marcador ni se reproduce ningún challenge (no
 *   llegó a crearse ninguno);
 * - **cámara bloqueada** con "Intentar nuevamente" y "Cómo habilitarla" (FR-036; pasos según el
 *   sistema y el modo de apertura, 006 · FR-015, `permission-help.ts`): aquí sí se
 *   reemplaza la pantalla entera, porque con el permiso denegado la cámara está realmente apagada
 *   (`cameraOn` la excluye) — no hay ningún `<video>` vivo que preservar;
 * - **error recuperable** (T071): igual que la discrepancia, una hoja **sobre** la cámara — nunca
 *   una pantalla que la reemplace. La primera versión de US1 sí la reemplazaba y remontaba el
 *   `<video>` en el reintento: como el efecto que abre la cámara solo se vuelve a ejecutar cuando
 *   cambia `cameraOn` (que aquí no cambia, la cámara nunca se apaga), el nodo nuevo se quedaba
 *   **sin `srcObject`** y la cámara "moría en silencio" (implementation-notes L5, aviso para T071).
 *   Mantener `CameraStage` montado todo el tiempo —mismo criterio que `ACTION_CHANGED`— es lo que
 *   lo evita: el reintento reutiliza el `<video>` que ya tiene el stream asignado.
 * - **error no recuperable**: pantalla completa (la cámara ya está apagada, FR-031).
 * - **listo para marcar** (`READY`, QA-7 de la 005): al terminar la cuenta regresiva de un `429`
 *   el error desaparece y una hoja **sobre** la cámara ofrece "Marcar" (la acción propuesta) e "Ir
 *   al inicio". La cámara sigue abierta pero no lee: volver a buscar exige tocar "Marcar", para que
 *   una marca nunca salga sola.
 *
 * `MarkingScreen` es la parte presentacional (recibe el estado ya calculado); `MarkingFlow` es la
 * que engancha el hook y la navegación.
 */
import { useCallback, useState } from 'react';

import type { AttendanceAction } from '@fermin/contracts';

import { Button } from '../../components/ui/button';
import { Screen } from '../../components/ui/screen';
import { Sheet } from '../../components/ui/sheet';
import { t } from '../../i18n';

import { CameraStage } from './camera-stage';
import { cameraHelpText } from './permission-help';
import { SuccessScreen } from './success-screen';
import { useMarking, type Marking, type UseMarkingOptions } from './use-marking';

export interface MarkingScreenProps {
  marking: Marking;
  /** Salir de la marcación (volver al inicio). */
  onExit: () => void;
}

export function MarkingScreen({ marking, onExit }: MarkingScreenProps) {
  const {
    state,
    videoRef,
    help,
    retryOffered,
    suggestSupport,
    autonomousRetry,
    retryDisabled,
    retryDisabledSeconds,
    locationNotice,
    showLocationExplanation,
    acknowledgeLocationExplanation,
    progress,
    roi,
    retry,
    confirmActionChanged,
  } = marking;
  const [howTo, setHowTo] = useState(false);

  if (state.phase === 'SUCCESS' && state.mark !== null) {
    return (
      <SuccessScreen
        mark={state.mark}
        onExit={onExit}
        footer={
          <Button data-testid="marking-success-home" variant="secondary" onClick={onExit}>
            {t('attendance.actions.backHome')}
          </Button>
        }
      />
    );
  }

  if (state.phase === 'CAMERA_BLOCKED') {
    return (
      <Screen
        data-testid="marking-camera-blocked"
        align="center"
        title={t('attendance.camera.deniedTitle')}
        lead={state.error?.message ?? t('attendance.camera.deniedBody')}
        footer={
          <>
            <Button data-testid="marking-camera-retry" onClick={retry}>
              {t('attendance.actions.retry')}
            </Button>
            <Button variant="ghost" onClick={() => setHowTo(true)}>
              {t('attendance.actions.howToEnable')}
            </Button>
            <Button variant="ghost" onClick={onExit}>
              {t('attendance.actions.backHome')}
            </Button>
          </>
        }
      >
        <Sheet
          open={howTo}
          onClose={() => setHowTo(false)}
          title={t('attendance.actions.howToEnable')}
          description={cameraHelpText()}
          footer={
            <Button variant="secondary" onClick={() => setHowTo(false)}>
              {t('common.buttons.understood')}
            </Button>
          }
        />
      </Screen>
    );
  }

  // Error **no recuperable**: la cámara ya está apagada (`cameraOn` la excluye), así que no hay
  // ningún `<video>` que preservar y una pantalla completa es segura.
  if (state.phase === 'ERROR' && state.error !== null && !state.error.recoverable) {
    return (
      <Screen
        data-testid="marking-error"
        data-code={state.error.code}
        align="center"
        title={t('attendance.error.title')}
        lead={state.error.message}
        footer={
          <Button variant="ghost" onClick={onExit}>
            {t('attendance.actions.backHome')}
          </Button>
        }
      />
    );
  }

  // FR-007/US2 (T036) y T071: la discrepancia de acción y el error **recuperable** se muestran
  // como hoja **sobre** la cámara, en vez de reemplazar la pantalla entera — la cámara sigue
  // abierta y el `<video>` nunca se desmonta, así que reintentar/confirmar no tiene que reabrirla
  // ni perder el enfoque (ver aviso arriba sobre por qué esto importa para T071).
  const actionChangedLabel =
    state.phase === 'ACTION_CHANGED' && state.proposedAction !== null
      ? state.proposedAction === 'CHECK_IN'
        ? t('attendance.actions.checkIn')
        : t('attendance.actions.checkOut')
      : null;
  const recoverableError = state.phase === 'ERROR' && state.error !== null ? state.error : null;
  const markLabel =
    state.proposedAction === 'CHECK_OUT'
      ? t('attendance.actions.checkOut')
      : t('attendance.actions.checkIn');

  return (
    <>
      <CameraStage
        phase={state.phase}
        videoRef={videoRef}
        queue={state.queue}
        progress={progress}
        help={help}
        retryOffered={retryOffered}
        suggestSupport={suggestSupport}
        autonomousRetry={autonomousRetry}
        locationNotice={locationNotice}
        roi={roi}
        onRetry={retry}
        onCancel={onExit}
      />
      <Sheet
        open={showLocationExplanation}
        onClose={acknowledgeLocationExplanation}
        title={t('attendance.location.permissionTitle')}
        description={t('attendance.location.permissionBody')}
        footer={
          <Button
            data-testid="marking-location-explanation-accept"
            onClick={acknowledgeLocationExplanation}
          >
            {t('common.buttons.understood')}
          </Button>
        }
      />
      <Sheet
        open={actionChangedLabel !== null}
        dismissible={false}
        onClose={onExit}
        title={t('attendance.actionChanged.title')}
        description={t('common.errors.ACTION_CHANGED')}
        footer={
          <>
            <Button data-testid="marking-action-changed-confirm" onClick={confirmActionChanged}>
              {actionChangedLabel}
            </Button>
            <Button variant="ghost" onClick={onExit}>
              {t('attendance.actions.cancel')}
            </Button>
          </>
        }
      />
      <Sheet
        open={recoverableError !== null}
        dismissible={false}
        onClose={onExit}
        title={t('attendance.error.title')}
        description={
          retryDisabled
            ? `${recoverableError?.message ?? ''} ${t('attendance.error.retryDisabled', { seconds: retryDisabledSeconds })}`
            : recoverableError?.message
        }
        footer={
          <>
            <Button
              data-testid="marking-error-retry"
              onClick={retry}
              disabled={retryDisabled}
            >
              {t('attendance.actions.retry')}
            </Button>
            <Button variant="ghost" onClick={onExit}>
              {t('attendance.actions.backHome')}
            </Button>
          </>
        }
      >
        {recoverableError !== null ? (
          <span data-testid="marking-error" data-code={recoverableError.code} className="sr-only" />
        ) : null}
        {/* El aviso de "contacta a tu administrador" (T072) ya lo pinta `CameraStage` de abajo, la
            misma que muestra `suggestSupport` mientras se busca sin haber fallado esta vez todavía;
            un segundo `marking-support` aquí duplicaría el testid. */}
      </Sheet>
      {/* QA-7: terminó la espera del `429`. Sin error; "Marcar" vuelve a buscar el marcador. */}
      <Sheet
        open={state.phase === 'READY'}
        dismissible={false}
        onClose={onExit}
        title={t('attendance.ready.title')}
        description={t('attendance.ready.body')}
        footer={
          <>
            <Button data-testid="marking-ready-mark" onClick={retry}>
              {markLabel}
            </Button>
            <Button variant="ghost" onClick={onExit}>
              {t('attendance.actions.backHome')}
            </Button>
          </>
        }
      >
        <span data-testid="marking-ready" className="sr-only" />
      </Sheet>
    </>
  );
}

export interface MarkingFlowProps {
  proposedAction: AttendanceAction | null;
  onExit: () => void;
  /** Opciones del hook (pruebas: cámara y API falsas). */
  options?: Omit<UseMarkingOptions, 'proposedAction'>;
}

export function MarkingFlow({ proposedAction, onExit, options }: MarkingFlowProps) {
  const marking = useMarking({ proposedAction, ...options });
  const exit = useCallback(() => {
    onExit();
  }, [onExit]);
  return <MarkingScreen marking={marking} onExit={exit} />;
}
